import { Download, Copy, Calendar } from 'lucide-react';
import { copyWithToast } from '../../utils/clipboard';

interface OATimelineDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function OATimelineDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: OATimelineDisplayProps) {
  // Parse result to extract 'output' if present
  const parseResult = (data: any) => {
    if (!data) return null;
    if (typeof data === 'object' && data.output) {
      return data.output;
    }
    return data;
  };

  const parsedResult = parseResult(result);

  // Check if this result contains timeline data
  if (!parsedResult || typeof parsedResult !== 'object') return null;

  // Check for timeline properties (matching patentloop-frontend)
  const isOATimeline = (parsedResult.timeline_events && Array.isArray(parsedResult.timeline_events)) ||
                       (parsedResult.events_chronological && Array.isArray(parsedResult.events_chronological));

  if (!isOATimeline) return null;

  const copyToClipboard = (text: string) => copyWithToast(text, showToast);

  const events = parsedResult.timeline_events || parsedResult.events_chronological || [];

  // Function to determine event color based on type
  const getEventColor = (eventType: string) => {
    const type = eventType?.toLowerCase() || '';
    if (type.includes('filing') || type.includes('filed')) return 'blue';
    if (type.includes('office action') || type.includes('rejection')) return 'red';
    if (type.includes('response') || type.includes('amendment')) return 'green';
    if (type.includes('allowance') || type.includes('issue')) return 'emerald';
    if (type.includes('interview') || type.includes('communication')) return 'purple';
    return 'gray';
  };

  const getBgColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 border-blue-300',
      red: 'bg-red-100 border-red-300',
      green: 'bg-green-100 border-green-300',
      emerald: 'bg-emerald-100 border-emerald-300',
      purple: 'bg-purple-100 border-purple-300',
      gray: 'bg-gray-100 border-gray-300'
    };
    return colors[color] || colors.gray;
  };

  const getTextColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'text-blue-900',
      red: 'text-red-900',
      green: 'text-green-900',
      emerald: 'text-emerald-900',
      purple: 'text-purple-900',
      gray: 'text-gray-900'
    };
    return colors[color] || colors.gray;
  };

  const getDotColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-500',
      red: 'bg-red-500',
      green: 'bg-green-500',
      emerald: 'bg-emerald-500',
      purple: 'bg-purple-500',
      gray: 'bg-gray-500'
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold">Prosecution Timeline</h3>
        </div>
      )}

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        {/* Summary */}
        {parsedResult.summary && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
            <div className="flex items-start gap-2">
              <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-2 text-blue-900">Timeline Summary</h4>
                <p className="text-sm whitespace-pre-wrap">{parsedResult.summary}</p>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Events */}
        {Array.isArray(events) && events.length > 0 ? (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>

            <div className="space-y-4">
              {events.map((event: any, idx: number) => {
                const color = getEventColor(event.type || event.event_type || event.event || '');
                return (
                  <div key={idx} className="relative pl-14">
                    {/* Timeline dot */}
                    <div className={`absolute left-4 w-5 h-5 rounded-full border-4 border-white ${getDotColor(color)}`}></div>

                    <div className={`p-4 rounded-lg border ${getBgColor(color)}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className={`font-semibold text-sm ${getTextColor(color)}`}>
                            {event.event || event.title || event.type || event.event_type || 'Event'}
                          </div>
                          {event.date && (
                            <div className="text-xs text-gray-600 mt-1">{event.date}</div>
                          )}
                        </div>
                        {event.description && (
                          <button
                            onClick={() => copyToClipboard(event.description)}
                            className="text-gray-600 hover:text-gray-900 p-1 ml-2"
                            title="Copy description"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {event.description && (
                        <p className="text-sm whitespace-pre-wrap mt-2">{event.description}</p>
                      )}

                      {event.details && (
                        <div className="text-sm mt-2 pt-2 border-t border-gray-300">
                          {typeof event.details === 'string' ? (
                            <p className="whitespace-pre-wrap">{event.details}</p>
                          ) : (
                            <div className="space-y-1">
                              {Object.entries(event.details).map(([key, value]: [string, any]) => (
                                <div key={key}>
                                  <span className="font-medium">{key}:</span> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {event.claims_affected && (
                        <div className="text-sm mt-2">
                          <span className="font-medium">Claims Affected:</span> {Array.isArray(event.claims_affected) ? event.claims_affected.join(', ') : event.claims_affected}
                        </div>
                      )}

                      {event.outcome && (
                        <div className="text-sm mt-2 bg-white bg-opacity-50 p-2 rounded">
                          <span className="font-medium">Outcome:</span> {event.outcome}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">No timeline events found</div>
        )}

        {/* Statistics */}
        {parsedResult.statistics && (
          <div className="mt-4 bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3">Statistics</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {parsedResult.statistics.total_actions && (
                <div>
                  <span className="font-medium">Total Actions:</span> {parsedResult.statistics.total_actions}
                </div>
              )}
              {parsedResult.statistics.office_actions && (
                <div>
                  <span className="font-medium">Office Actions:</span> {parsedResult.statistics.office_actions}
                </div>
              )}
              {parsedResult.statistics.applicant_responses && (
                <div>
                  <span className="font-medium">Applicant Responses:</span> {parsedResult.statistics.applicant_responses}
                </div>
              )}
              {parsedResult.statistics.duration && (
                <div>
                  <span className="font-medium">Duration:</span> {parsedResult.statistics.duration}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
